import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, Typography, Chip, Button, Dialog, DialogHeader, DialogBody, DialogFooter, Input } from '@material-tailwind/react';
import { useAuth } from '../../AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCar, faMoneyBillWave, faCalendarAlt,
    faFileInvoiceDollar, faCheckCircle,
    faMapMarkerAlt, faUser, faTachometerAlt, faWrench,
    faLink, faHistory, faCalendarDay
} from '@fortawesome/free-solid-svg-icons';
import { rootUrl } from '../../../../config/config';

export default function JobCardOverview({ jobCard, onBack, onRefresh, permissions = [] }) {
    const { user } = useAuth();
    const token = user?.token || localStorage.getItem('authToken');

    const [changeDateDialogOpen, setChangeDateDialogOpen] = useState(false);
    const [newInvoiceDate, setNewInvoiceDate] = useState('');
    const [changeDateLoading, setChangeDateLoading] = useState(false);
    const [changeDateError, setChangeDateError] = useState(null);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES'
        }).format(parseFloat(amount) || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const buildOwnerTransactionPath = (ownerCode, transId) => (
        ownerCode && transId ? `/owners/transactions/${encodeURIComponent(ownerCode)}/${encodeURIComponent(transId)}` : null
    );

    const getDocumentNumber = (value) => (
        String(value || '').split(':').pop() || value || '-'
    );

    const formatTimestamp = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    const getPaymentStatusColor = (status) => {
        const statusMap = {
            'PENDING': 'amber',
            'PAID': 'green',
            'PARTIALLY_PAID': 'orange',
            'OVERDUE': 'red'
        };
        return statusMap[status] || 'gray';
    };

    return (
        <div className="py-6 space-y-6">
            {/* Header Summary */}
            <Card className="bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] shadow-sm">
                <div className="p-6">
                    {/* Key Metrics */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="bg-white/80 rounded-lg p-4">
                            <Typography variant="small" className="text-gray-600 mb-1">
                                Total Cost
                            </Typography>
                            <Typography variant="h5" className="font-bold text-[#FF5733] text-right">
                                {formatCurrency(jobCard?.total_cost)}
                            </Typography>
                        </div>
                        <div className="bg-white/80 rounded-lg p-4">
                            <Typography variant="small" className="text-gray-600 mb-1">
                                Parts Cost
                            </Typography>
                            <Typography variant="h6" className="font-bold text-blue-600 text-right">
                                {formatCurrency(jobCard?.parts_cost)}
                            </Typography>
                        </div>
                        <div className="bg-white/80 rounded-lg p-4">
                            <Typography variant="small" className="text-gray-600 mb-1">
                                Labor & Other Costs
                            </Typography>
                            <Typography variant="h6" className="font-bold text-green-600 text-right">
                                {formatCurrency(parseFloat(jobCard?.labor_cost || 0) + parseFloat(jobCard?.charges_cost || 0) + parseFloat(jobCard?.external_mechanic_cost || 0) + parseFloat(jobCard?.other_costs || 0))}
                            </Typography>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Vehicle & Service Location */}
            <Card className="shadow-sm">
                <div className="p-6">
                    <div className="flex items-center gap-2 mb-6">
                        <FontAwesomeIcon icon={faCar} className="text-[#FF5733] text-xl" />
                        <Typography variant="h6" className="font-bold text-[#2C3E50]">
                            Vehicle & Service Information
                        </Typography>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                Plate Number
                            </Typography>
                            {jobCard?.plate_no ? (
                                <Link to={`/cars/${encodeURIComponent(jobCard.plate_no)}`} className="text-lg font-bold text-[#FF5733] hover:text-[#e64520] hover:underline">
                                    {jobCard.plate_no}
                                </Link>
                            ) : (
                                <Typography className="text-lg font-bold text-[#FF5733]">N/A</Typography>
                            )}
                        </div>
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                Vehicle Make/Model
                            </Typography>
                            <Typography className="font-bold text-gray-900">
                                {jobCard?.make} {jobCard?.model} {jobCard?.year && `(${jobCard.year})`}
                            </Typography>
                        </div>
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                Owner
                            </Typography>
                            <Typography className="font-bold text-gray-900">
                                {jobCard?.owner_name ? `${jobCard.owner_name} (${jobCard.owner_code})` : jobCard?.owner_code || 'N/A'}
                            </Typography>
                        </div>
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                Driver ID
                            </Typography>
                            <Typography className="font-bold text-gray-900">
                                {jobCard?.driver_id || 'N/A'}
                            </Typography>
                        </div>
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                <FontAwesomeIcon icon={faTachometerAlt} className="mr-2" />
                                Mileage at Service
                            </Typography>
                            <Typography className="font-bold text-gray-900 text-right">
                                {jobCard?.mileage_at_service ? `${jobCard.mileage_at_service.toLocaleString()} km` : 'Not recorded'}
                            </Typography>
                        </div>
                        <div className="col-span-1 md:col-span-3 pt-4 mt-2">
                            <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2" />
                                Service Location
                            </Typography>
                            <div className="flex flex-wrap gap-2 items-center">
                                <Chip
                                    value={jobCard?.service_location?.toUpperCase() || 'UNKNOWN'}
                                    color={jobCard?.service_location === 'internal' ? 'green' : 'blue'}
                                    className="font-semibold"
                                />
                                {jobCard?.service_center_name && (
                                    <>
                                        <Typography className="font-bold text-gray-900">
                                            {jobCard.service_center_name}
                                        </Typography>
                                        {jobCard?.service_center_contact && (
                                            <Typography variant="small" className="text-gray-600">
                                                ({jobCard.service_center_contact})
                                            </Typography>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Work Details */}
            <Card className="shadow-sm">
                <div className="p-6">
                    <div className="flex items-center gap-2 mb-6">
                        <FontAwesomeIcon icon={faWrench} className="text-[#FF5733] text-xl" />
                        <Typography variant="h6" className="font-bold text-[#2C3E50]">
                            Work Details
                        </Typography>
                    </div>
                    <div className="space-y-4">
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-2">
                                Diagnosis
                            </Typography>
                            <div className="bg-gray-50 p-3 rounded-lg">
                                <Typography className="text-gray-900">
                                    {jobCard?.diagnosis || 'No diagnosis provided'}
                                </Typography>
                            </div>
                        </div>
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-2">
                                Work Performed
                            </Typography>
                            <div className="bg-gray-50 p-3 rounded-lg">
                                <Typography className="text-gray-900">
                                    {jobCard?.work_performed || 'No work details provided'}
                                </Typography>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                    <FontAwesomeIcon icon={faUser} className="mr-2" />
                                    Mechanic (Internal)
                                </Typography>
                                <Typography className="font-bold text-gray-900">
                                    {jobCard?.mechanic_name || 'N/A'}
                                </Typography>
                            </div>
                            <div>
                                <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                    <FontAwesomeIcon icon={faUser} className="mr-2" />
                                    Mechanic (External)
                                </Typography>
                                <Typography className="font-bold text-gray-900">
                                    {jobCard?.external_mechanic_name || 'N/A'}
                                </Typography>
                            </div>
                        </div>
                        {jobCard?.status_change_notes && (
                            <div>
                                <Typography variant="small" className="text-gray-600 font-semibold mb-2">
                                    Status Change Notes
                                </Typography>
                                <div className="bg-blue-50 p-3 rounded-lg">
                                    <Typography className="text-gray-900 text-sm">
                                        {jobCard.status_change_notes}
                                    </Typography>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </Card>

            {/* Payment & Reimbursement */}
            <Card className="shadow-sm">
                <div className="p-6">
                    <div className="flex items-center gap-2 mb-6">
                        <FontAwesomeIcon icon={faMoneyBillWave} className="text-[#FF5733] text-xl" />
                        <Typography variant="h6" className="font-bold text-[#2C3E50]">
                            Payment & Reimbursement
                        </Typography>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                Paid By
                            </Typography>
                            <Chip
                                value={jobCard?.paid_by || 'UNKNOWN'}
                                color={jobCard?.paid_by === 'OFFICE' ? 'blue' : 'green'}
                                className="font-semibold w-fit"
                            />
                        </div>
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                Payment Status
                            </Typography>
                            <Chip
                                value={jobCard?.payment_status || 'UNKNOWN'}
                                color={getPaymentStatusColor(jobCard?.payment_status)}
                                className="font-semibold w-fit"
                            />
                        </div>
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                Requires Reimbursement
                            </Typography>
                            <Chip
                                value={jobCard?.requires_reimbursement ? 'YES' : 'NO'}
                                color={jobCard?.requires_reimbursement ? 'amber' : 'gray'}
                                className="font-semibold w-fit"
                            />
                        </div>
                        {jobCard?.requires_reimbursement && (
                            <>
                                <div>
                                    <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                        Reimbursement Amount
                                    </Typography>
                                    <Typography className="font-bold text-[#FF5733] text-right">
                                        {formatCurrency(jobCard?.reimbursement_amount)}
                                    </Typography>
                                </div>
                                <div>
                                    <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                        Reimbursement Status
                                    </Typography>
                                    <Typography className="font-bold text-gray-900">
                                        {jobCard?.reimbursement_status || 'N/A'}
                                    </Typography>
                                </div>
                                <div>
                                    <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                        Reimbursed from Remittance
                                    </Typography>
                                    <div className="flex gap-2 items-center">
                                        <Chip
                                            value={jobCard?.reimbursed_from_remittance ? 'YES' : 'NO'}
                                            color={jobCard?.reimbursed_from_remittance ? 'green' : 'gray'}
                                            className="font-semibold w-fit"
                                        />
                                        {jobCard?.reimbursed_week_key && (
                                            <Typography variant="small" className="text-gray-600">
                                                (Week: {jobCard.reimbursed_week_key})
                                            </Typography>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </Card>

            {/* Invoice Information */}
            {jobCard?.invoiced && (
                <Card className="shadow-sm bg-purple-50/30">
                    <div className="p-6">
                        <div className="flex items-center gap-2 mb-6">
                            <FontAwesomeIcon icon={faFileInvoiceDollar} className="text-purple-500 text-xl" />
                            <Typography variant="h6" className="font-bold text-[#2C3E50]">
                                Invoice Information
                            </Typography>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                    Invoice Reference
                                </Typography>
                                {jobCard?.invoice_reference && jobCard?.owner_code ? (
                                    <Link
                                        to={`/owners/statements/${encodeURIComponent(jobCard.owner_code)}`}
                                        className="text-lg font-bold text-purple-600 hover:text-purple-800 hover:underline"
                                    >
                                        {jobCard.invoice_reference}
                                    </Link>
                                ) : (
                                    <Typography className="text-lg font-bold text-purple-600">
                                        {jobCard?.invoice_reference || 'N/A'}
                                    </Typography>
                                )}
                            </div>
                            <div>
                                <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                    Invoice Date
                                </Typography>
                                <div className="flex items-center gap-2">
                                    <Typography className="font-bold text-gray-900">
                                        {formatDate(jobCard?.invoice_date)}
                                    </Typography>
                                    {permissions.includes('owner_invoice.edit') && (
                                        <Button
                                            size="sm"
                                            variant="outlined"
                                            className="py-1 px-2 text-xs"
                                            onClick={() => {
                                                setNewInvoiceDate(jobCard?.invoice_date ? new Date(jobCard.invoice_date).toISOString().split('T')[0] : '');
                                                setChangeDateError(null);
                                                setChangeDateDialogOpen(true);
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faCalendarDay} className="mr-1" />
                                            Change
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <div>
                                <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                    Invoice Status
                                </Typography>
                                <Chip
                                    value={jobCard?.invoice_skipped ? 'SKIPPED' : 'INVOICED'}
                                    color={jobCard?.invoice_skipped ? 'amber' : 'green'}
                                    icon={<FontAwesomeIcon icon={faCheckCircle} />}
                                    className="font-semibold w-fit"
                                />
                            </div>
                            {jobCard?.invoice_skip_notes && (
                                <div className="col-span-1 md:col-span-3">
                                    <Typography variant="small" className="text-gray-600 font-semibold mb-2">
                                        Invoice Skip Notes
                                    </Typography>
                                    <div className="bg-amber-50 p-3 rounded-lg">
                                        <Typography className="text-gray-900 text-sm">
                                            {jobCard.invoice_skip_notes}
                                        </Typography>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </Card>
            )}

            {/* Change Invoice Date Dialog */}
            <Dialog
                open={changeDateDialogOpen}
                handler={() => setChangeDateDialogOpen(false)}
                className="bg-white"
            >
                <DialogHeader>Change Invoice Date</DialogHeader>
                <DialogBody>
                    <Typography variant="paragraph" className="mb-4">
                        Select a new invoice date. This will update the job card and all related owner transactions.
                    </Typography>
                    {changeDateError && (
                        <Typography variant="small" className="text-red-600 mb-2">{changeDateError}</Typography>
                    )}
                    <Input
                        type="date"
                        label="New Invoice Date"
                        value={newInvoiceDate}
                        onChange={(e) => setNewInvoiceDate(e.target.value)}
                        disabled={changeDateLoading}
                    />
                </DialogBody>
                <DialogFooter>
                    <Button
                        variant="text"
                        onClick={() => setChangeDateDialogOpen(false)}
                        className="mr-2"
                        disabled={changeDateLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={async () => {
                            if (!newInvoiceDate) {
                                setChangeDateError('Please select a date');
                                return;
                            }
                            setChangeDateLoading(true);
                            setChangeDateError(null);
                            try {
                                const res = await axios.post(
                                    `${rootUrl}/api/job-cards/${jobCard?.id}/change-invoice-date`,
                                    { new_invoice_date: newInvoiceDate },
                                    { headers: { Authorization: `Bearer ${token}` } }
                                );
                                if (res.data?.success) {
                                    setChangeDateDialogOpen(false);
                                    onRefresh?.();
                                } else {
                                    setChangeDateError(res.data?.message || 'Failed to update');
                                }
                            } catch (err) {
                                setChangeDateError(err.response?.data?.message || err.message || 'Failed to update');
                            } finally {
                                setChangeDateLoading(false);
                            }
                        }}
                        disabled={changeDateLoading}
                    >
                        {changeDateLoading ? 'Updating...' : 'Update'}
                    </Button>
                </DialogFooter>
            </Dialog>

            {/* Important Dates */}
            <Card className="shadow-sm">
                <div className="p-6">
                    <div className="flex items-center gap-2 mb-6">
                        <FontAwesomeIcon icon={faCalendarAlt} className="text-[#FF5733] text-xl" />
                        <Typography variant="h6" className="font-bold text-[#2C3E50]">
                            Important Dates
                        </Typography>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6">
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                Opened Date
                            </Typography>
                            <Typography className="font-bold text-gray-900">
                                {formatDate(jobCard?.opened_date)}
                            </Typography>
                        </div>
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                Completed Date
                            </Typography>
                            <Typography className="font-bold text-gray-900">
                                {formatDate(jobCard?.completed_date)}
                            </Typography>
                        </div>
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                Closed Date
                            </Typography>
                            <Typography className="font-bold text-gray-900">
                                {formatDate(jobCard?.closed_date)}
                            </Typography>
                        </div>
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                Week
                            </Typography>
                            <Typography className="font-bold text-gray-900">
                                {jobCard?.week_key || 'N/A'}
                            </Typography>
                        </div>
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                Invoice Week
                            </Typography>
                            <Typography className="font-bold text-gray-900">
                                {jobCard?.invoice_week || 'N/A'}
                            </Typography>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Related Records & Links */}
            <Card className="shadow-sm">
                <div className="p-6">
                    <div className="flex items-center gap-2 mb-6">
                        <FontAwesomeIcon icon={faLink} className="text-[#FF5733] text-xl" />
                        <Typography variant="h6" className="font-bold text-[#2C3E50]">
                            Related Records & Links
                        </Typography>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                Related Expense ID
                            </Typography>
                            <Typography className="font-bold text-gray-900">
                                {jobCard?.related_expense_id || 'None'}
                            </Typography>
                        </div>
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                Related Transaction ID
                            </Typography>
                            {buildOwnerTransactionPath(jobCard?.owner_code, jobCard?.related_transaction_id) ? (
                                <Link
                                    to={buildOwnerTransactionPath(jobCard.owner_code, jobCard.related_transaction_id)}
                                    className="font-bold text-[#FF5733] hover:text-[#e64520] hover:underline"
                                >
                                    {getDocumentNumber(jobCard.related_transaction_id)}
                                </Link>
                            ) : (
                                <Typography className="font-bold text-gray-900">
                                    {jobCard?.related_transaction_id || 'None'}
                                </Typography>
                            )}
                        </div>
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                Cashbook Entry ID
                            </Typography>
                            <Typography className="font-bold text-gray-900">
                                {jobCard?.cashbook_entry_id || 'None'}
                            </Typography>
                        </div>
                        {jobCard?.drive_link && (
                            <div className="col-span-1 md:col-span-2 lg:col-span-3">
                                <Typography variant="small" className="text-gray-600 font-semibold mb-2">
                                    Drive Link
                                </Typography>
                                <a
                                    href={jobCard.drive_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 hover:text-blue-800 underline font-semibold"
                                >
                                    <FontAwesomeIcon icon={faLink} className="mr-2" />
                                    {jobCard.drive_link}
                                </a>
                            </div>
                        )}
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                Timeline Posted
                            </Typography>
                            <Chip
                                value={jobCard?.timeline_posted ? 'YES' : 'NO'}
                                color={jobCard?.timeline_posted ? 'green' : 'gray'}
                                size="sm"
                                className="w-fit"
                            />
                        </div>
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                To Invoice
                            </Typography>
                            <Chip
                                value={jobCard?.to_invoice ? 'YES' : 'NO'}
                                color={jobCard?.to_invoice ? 'amber' : 'gray'}
                                size="sm"
                                className="w-fit"
                            />
                        </div>
                    </div>
                </div>
            </Card>

            {/* Audit Trail */}
            <Card className="shadow-sm bg-gray-50">
                <div className="p-6">
                    <div className="flex items-center gap-2 mb-6">
                        <FontAwesomeIcon icon={faHistory} className="text-gray-600 text-xl" />
                        <Typography variant="h6" className="font-bold text-gray-700">
                            Audit Trail
                        </Typography>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                Created By
                            </Typography>
                            <Typography className="font-bold text-gray-900">
                                {jobCard?.input_by || 'Unknown'}
                            </Typography>
                            <Typography variant="small" className="text-gray-600">
                                {formatTimestamp(jobCard?.input_date)}
                            </Typography>
                        </div>
                        <div>
                            <Typography variant="small" className="text-gray-600 font-semibold mb-1">
                                Last Modified By
                            </Typography>
                            <Typography className="font-bold text-gray-900">
                                {jobCard?.edit_by || 'N/A'}
                            </Typography>
                            <Typography variant="small" className="text-gray-600">
                                {formatTimestamp(jobCard?.edit_date)}
                            </Typography>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
}
